// src/views/contasReceber/ReceberParcelaModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import contasBancariasService from "../../services/contasBancariasService";

const fmtMoeda = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const today = () => new Date().toISOString().split("T")[0];

function diasAtraso(dtVenc) {
  if (!dtVenc) return 0;
  const v = new Date(String(dtVenc).split("T")[0] + "T00:00:00");
  const h = new Date(); h.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((h - v) / 86400000));
}

export default function ReceberParcelaModal({
  show, onClose, onRecebido, parcela, idCondominio,
  descricao = "", cobradoBoleto = false,
}) {
  const [form, setForm] = useState({
    idContaBancaria: "",
    tpRecebimento: "PIX",
    vlrRecebido: "",
    dtRecebimento: today(),
    vlrMulta: "0.00",
    vlrJuro: "0.00",
    vlrDesconto: "0.00",
    observacao: "",
  });
  const [contas, setContas] = useState([]);
  const [politica, setPolitica] = useState(null); // { pctMulta, pctJuroDia, tpMulta, tpJuro }
  const [aplicouSugestao, setAplicouSugestao] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Carrega contas bancárias + política de multa/juros do condomínio
  useEffect(() => {
    if (!show || !idCondominio) return;
    let cancelado = false;
    (async () => {
      try {
        const r = await contasBancariasService.listar({ idCondominio, situacao: "ativo" });
        if (cancelado) return;
        setContas(r.data || []);
        if (r.data?.length) {
          setForm(p => ({ ...p, idContaBancaria: String(r.data[0].id) }));
          // Tenta carregar política de multa/juros da conta primária
          try {
            const cfg = await contasBancariasService.buscarConfigBoleto(r.data[0].id);
            if (cancelado) return;
            if (cfg?.data) {
              setPolitica({
                tpMulta:    cfg.data.tpMulta || "Percentual",
                vlrMulta:   parseFloat(cfg.data.vlrMulta || 0),
                tpJuro:     cfg.data.tpJuro || "Mensal",
                vlrJuroDia: parseFloat(cfg.data.vlrJuroDia || 0),
              });
            }
          } catch { /* política opcional */ }
        }
      } catch { /* lista opcional */ }
    })();
    setForm(p => ({ ...p, vlrRecebido: parcela?.vlrParcela || "" }));
    setErrors({});
    setAplicouSugestao(false);
    return () => { cancelado = true; };
  }, [show, parcela, idCondominio]);

  const atraso = useMemo(() => diasAtraso(parcela?.dtVencimento), [parcela]);

  // Sugestão automática de multa/juros baseada na política do condomínio
  const sugestao = useMemo(() => {
    if (!politica || atraso === 0 || !parcela) return null;
    const vlrOrig = parseFloat(parcela.vlrParcela || 0);
    let multa = 0, juros = 0;
    if (politica.tpMulta === "Percentual") multa = vlrOrig * (politica.vlrMulta / 100);
    else if (politica.tpMulta === "ValorFixo") multa = politica.vlrMulta;
    if (politica.vlrJuroDia > 0) {
      const txDia = politica.tpJuro === "Mensal" ? politica.vlrJuroDia / 30 : politica.vlrJuroDia;
      juros = vlrOrig * (txDia / 100) * atraso;
    }
    return {
      vlrMulta: parseFloat(multa.toFixed(2)),
      vlrJuro:  parseFloat(juros.toFixed(2)),
      diasAtraso: atraso,
    };
  }, [politica, atraso, parcela]);

  const aplicarSugestao = () => {
    if (!sugestao) return;
    setForm(p => ({
      ...p,
      vlrMulta: sugestao.vlrMulta.toFixed(2),
      vlrJuro:  sugestao.vlrJuro.toFixed(2),
    }));
    setAplicouSugestao(true);
  };

  const set = (f, v) => { setForm(p => ({ ...p, [f]: v })); setErrors(p => ({ ...p, [f]: undefined })); };

  const vlrFinal = parseFloat(form.vlrRecebido || 0)
                 + parseFloat(form.vlrMulta || 0)
                 + parseFloat(form.vlrJuro || 0)
                 - parseFloat(form.vlrDesconto || 0);

  const validate = () => {
    const e = {};
    if (!form.idContaBancaria) e.idContaBancaria = "Selecione a conta bancária";
    if (!form.vlrRecebido || parseFloat(form.vlrRecebido) <= 0) e.vlrRecebido = "Valor obrigatório";
    if (!form.dtRecebimento) e.dtRecebimento = "Data obrigatória";
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleReceber = async () => {
    if (!validate()) return;
    setSaving(true);
    try { await onRecebido(form); }
    catch (e) { alert(e.message); }
    setSaving(false);
  };

  if (!show) return null;

  // Se está em boleto, mostra apenas alerta de bloqueio
  if (cobradoBoleto) {
    return (
      <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.55)" }} tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: 38, height: 38, background: "rgba(255,255,255,.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-receipt" style={{ fontSize: 18, color: "#fff" }} />
                </div>
                <div>
                  <h5 className="modal-title mb-0">Parcela vinculada a Boleto</h5>
                  <small style={{ color: "rgba(255,255,255,.6)", fontSize: 12 }}>{descricao}</small>
                </div>
              </div>
              <button className="btn-close btn-close-white" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="alert alert-warning d-flex align-items-start gap-3" style={{ borderLeft: "4px solid #F59E0B" }}>
                <i className="bi bi-info-circle-fill" style={{ fontSize: 24, color: "#B45309", marginTop: 2 }} />
                <div>
                  <strong>Esta parcela está vinculada a um boleto ativo.</strong>
                  <p className="mt-2 mb-2" style={{ color: "#78350F", fontSize: 14 }}>
                    Para manter a consistência entre o banco, o boleto e o Contas a Receber,
                    a baixa deve ser feita pelo módulo <strong>Cobranças → Controle de Boletos</strong>:
                  </p>
                  <ul style={{ color: "#78350F", fontSize: 13, marginBottom: 0 }}>
                    <li>Se o boleto foi pago via PIX, registre a baixa manual em Controle de Boletos.</li>
                    <li>Se o boleto foi pago via banco, importe o arquivo CNAB de retorno.</li>
                    <li>Para tornar essa parcela avulsa, cancele o boleto correspondente primeiro.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={onClose}>Entendi</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.55)" }} tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex align-items-center gap-3">
              <div style={{ width: 38, height: 38, background: "rgba(255,255,255,.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="bi bi-cash-coin" style={{ fontSize: 18, color: "#fff" }} />
              </div>
              <div>
                <h5 className="modal-title mb-0">Registrar Recebimento</h5>
                <small style={{ color: "rgba(255,255,255,.6)", fontSize: 12 }}>Parcela {parcela?.nrParcela} — {descricao}</small>
              </div>
            </div>
            <button className="btn-close btn-close-white" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* Info parcela */}
            <div style={{
              background: atraso > 0 ? "#FEE2E2" : "#EFF6FF",
              border: `1px solid ${atraso > 0 ? "#FCA5A5" : "#BFDBFE"}`,
              borderRadius: 8, padding: "10px 16px", marginBottom: 16,
              display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center",
            }}>
              <div>
                <span style={{ fontSize: 11, color: "var(--gray-500)" }}>Valor da Parcela</span>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--primary)" }}>{fmtMoeda(parcela?.vlrParcela)}</div>
              </div>
              <div>
                <span style={{ fontSize: 11, color: "var(--gray-500)" }}>Vencimento</span>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {parcela?.dtVencimento ? new Date(String(parcela.dtVencimento).split("T")[0] + "T12:00:00").toLocaleDateString("pt-BR") : "—"}
                </div>
              </div>
              <div>
                <span style={{ fontSize: 11, color: "var(--gray-500)" }}>Status</span>
                <div style={{ fontSize: 14, fontWeight: 600, color: parcela?.status === "Vencido" ? "#991B1B" : "#854D0E" }}>
                  {parcela?.status}
                </div>
              </div>
              {atraso > 0 && (
                <div>
                  <span style={{ fontSize: 11, color: "#991B1B" }}>Dias em atraso</span>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#991B1B" }}>
                    <i className="bi bi-clock-history me-1" />{atraso} dia{atraso !== 1 ? "s" : ""}
                  </div>
                </div>
              )}
            </div>

            {/* Sugestão automática de multa/juros */}
            {sugestao && !aplicouSugestao && (
              <div className="alert alert-info d-flex align-items-center justify-content-between mb-3">
                <div style={{ fontSize: 13 }}>
                  <i className="bi bi-lightbulb me-2" />
                  <strong>Sugestão baseada na política do condomínio:</strong>
                  <span className="ms-2">
                    Multa <strong>{fmtMoeda(sugestao.vlrMulta)}</strong> + Juros <strong>{fmtMoeda(sugestao.vlrJuro)}</strong>
                  </span>
                </div>
                <button type="button" className="btn btn-sm btn-info" onClick={aplicarSugestao}>
                  <i className="bi bi-check-circle me-1" />Aplicar
                </button>
              </div>
            )}
            {aplicouSugestao && (
              <div className="alert alert-success py-2 mb-3" style={{ fontSize: 12.5 }}>
                <i className="bi bi-check-circle me-1" />Sugestão aplicada. Ajuste se necessário.
              </div>
            )}

            <div className="form-section">
              <div className="form-section-title"><i className="bi bi-bank" /> Dados do Recebimento</div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Conta Bancária <span className="required-mark">*</span></label>
                  <select className={`form-select ${errors.idContaBancaria ? "is-invalid" : ""}`}
                    value={form.idContaBancaria} onChange={e => set("idContaBancaria", e.target.value)}>
                    <option value="">— Selecione —</option>
                    {contas.map(c => <option key={c.id} value={c.id}>{c.nmConta} — Saldo: {fmtMoeda(c.saldoAtual)}</option>)}
                  </select>
                  {errors.idContaBancaria && <div className="invalid-feedback">{errors.idContaBancaria}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Forma de Recebimento</label>
                  <select className="form-select" value={form.tpRecebimento} onChange={e => set("tpRecebimento", e.target.value)}>
                    {["PIX","Transferencia","TED","DOC","Boleto","Dinheiro","Cheque","Cartao","Outro"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Data do Recebimento <span className="required-mark">*</span></label>
                  <input type="date" className={`form-control ${errors.dtRecebimento ? "is-invalid" : ""}`}
                    value={form.dtRecebimento} onChange={e => set("dtRecebimento", e.target.value)} />
                  {errors.dtRecebimento && <div className="invalid-feedback">{errors.dtRecebimento}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Valor Recebido (R$) <span className="required-mark">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text">R$</span>
                    <input className={`form-control ${errors.vlrRecebido ? "is-invalid" : ""}`} type="number" step="0.01"
                      value={form.vlrRecebido} onChange={e => set("vlrRecebido", e.target.value)} />
                  </div>
                  {errors.vlrRecebido && <div className="invalid-feedback">{errors.vlrRecebido}</div>}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Multa (R$)</label>
                  <div className="input-group">
                    <span className="input-group-text">R$</span>
                    <input className="form-control" type="number" step="0.01"
                      value={form.vlrMulta} onChange={e => set("vlrMulta", e.target.value)} />
                  </div>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Juros (R$)</label>
                  <div className="input-group">
                    <span className="input-group-text">R$</span>
                    <input className="form-control" type="number" step="0.01"
                      value={form.vlrJuro} onChange={e => set("vlrJuro", e.target.value)} />
                  </div>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Desconto (R$)</label>
                  <div className="input-group">
                    <span className="input-group-text">R$</span>
                    <input className="form-control" type="number" step="0.01"
                      value={form.vlrDesconto} onChange={e => set("vlrDesconto", e.target.value)} />
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">Observações</label>
                  <input className="form-control" value={form.observacao}
                    onChange={e => set("observacao", e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer d-flex justify-content-between">
            <div style={{ fontSize: 14, color: "var(--gray-600)" }}>
              Total a receber: <strong style={{ color: "#166534", fontSize: 16 }}>{fmtMoeda(vlrFinal)}</strong>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                <i className="bi bi-x-circle" /> Cancelar
              </button>
              <button className="btn btn-success" onClick={handleReceber} disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-2" />Processando...</>
                  : <><i className="bi bi-check2-circle" /> Confirmar Recebimento</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
